import React, { useState, useEffect } from 'react';
import { t } from '../../utils/i18n';

const DEVICE_TYPES = [
  { value: 'fingerprint', label: 'Fingerprint' },
  { value: 'signature', label: 'Signature Pad' }
];

const BiometricSettings = ({ api }) => {
  const [devices, setDevices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [showEnroll, setShowEnroll] = useState(false);
  const [enrollDeviceId, setEnrollDeviceId] = useState(null);
  const [selectedPersonnelId, setSelectedPersonnelId] = useState(null);
  const [biometricType, setBiometricType] = useState('fingerprint');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    device_id: '',
    model: '',
    sdk_version: '',
    device_type: '',
    is_active: false,
    location_id: '',
    department_id: ''
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const resp = await api.get('/biometric_devices');
      const list = (resp && resp.data && resp.data.biometric_devices) || resp.biometric_devices || resp || [];
      setDevices(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to fetch devices', err);
      setDevices([]);
    }

    try {
      const locs = await api.get('/locations');
      setLocations(locs || []);
    } catch (err) {
      setLocations([]);
    }

    try {
      const deps = await api.get('/departments');
      setDepartments(deps || []);
    } catch (err) {
      setDepartments([]);
    }

    // fetch users with personnel (for enrollment)
    try {
      const u = await api.get('/users?personnel=true');
      setUsers(u || []);
    } catch (err) {
      console.error('Failed to fetch users for enrollment', err);
      setUsers([]);
    }

    setLoading(false);
  };

  const handleAdd = () => {
    setFormData({ device_id: '', model: '', sdk_version: '', device_type: '', is_active: false, location_id: '', department_id: '' });
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (d) => {
    setFormData({
      device_id: d.device_id || '',
      model: d.model || '',
      sdk_version: d.sdk_version || '',
      device_type: d.device_type || '',
      is_active: !!d.is_active,
      location_id: d.location_id || '',
      department_id: d.department_id || ''
    });
    setEditingId(d.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this biometric device?')) return;
    setLoading(true);
    try {
      await api.delete(`/biometric_devices/${id}`);
      setDevices(devices.filter(d => d.id !== id));
    } catch (err) {
      console.error('Failed to delete device', err);
      alert('Failed to delete device');
    } finally {
      setLoading(false);
    }
  };

  const openEnrollModal = (deviceId) => {
    setEnrollDeviceId(deviceId);
    setSelectedPersonnelId(null);
    setBiometricType('fingerprint');
    setShowEnroll(true);
  };

  const handleEnroll = async (e) => {
    e.preventDefault();
    if (!selectedPersonnelId || !enrollDeviceId) return alert('Select personnel and device');
    setLoading(true);
    try {
      // If `data` param is omitted, server will generate simulated data
      const payload = {
        army_personnel_id: selectedPersonnelId,
        biometric_device_id: enrollDeviceId,
        biometric_type: biometricType
      };
      const { response, data } = await api.post('/biometric_data', payload);
      if (response && response.ok) {
        alert('Biometric data enrolled successfully');
        setShowEnroll(false);
      } else {
        const err = (data && data.error) || (data && data.message) || 'Enrollment failed';
        alert(err);
      }
    } catch (err) {
      console.error('Failed to enroll biometric data', err);
      alert('Failed to enroll biometric data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingId) {
        const { response, data } = await api.put(`/biometric_devices/${editingId}`, { biometric_device: formData });
        if (response && response.ok) {
          setDevices(devices.map(d => (d.id === editingId ? data : d)));
        } else {
          alert('Failed to update device');
        }
      } else {
        const { response, data } = await api.post('/biometric_devices', { biometric_device: formData });
        if (response && response.ok) {
          setDevices([...devices, data]);
        } else {
          alert('Failed to create device');
        }
      }
      setShowForm(false);
      setEditingId(null);
    } catch (err) {
      console.error('Failed to save device', err);
      alert('Failed to save device');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="dFlex baseTableHd mb_16">
        <h2 className="txt20 fwMedium colorBlack">Biometric Devices</h2>
        <div style={{ marginLeft: 'auto' }}>
          <button className="btnAdd" onClick={handleAdd}>Add Device</button>
        </div>
      </div>

      {showForm && (
        <div className="mb_24 baseTableWrap">
          <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
            <div className="row">
              <div className="col-md-4 mb_20">
                <label className="mb_8">Device ID</label>
                <input type="text" className="form-control" value={formData.device_id} onChange={(e) => setFormData({ ...formData, device_id: e.target.value })} required />
              </div>
              <div className="col-md-4 mb_20">
                <label className="mb_8">Model</label>
                <input type="text" className="form-control" value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })} />
              </div>
              <div className="col-md-4 mb_20">
                <label className="mb_8">SDK Version</label>
                <input type="text" className="form-control" value={formData.sdk_version} onChange={(e) => setFormData({ ...formData, sdk_version: e.target.value })} />
              </div>

              <div className="col-md-4 mb_20">
                <label className="mb_8">Device Type</label>
                <select className="form-control" value={formData.device_type} onChange={(e) => setFormData({ ...formData, device_type: e.target.value })}>
                  <option value="">Select</option>
                  {DEVICE_TYPES.map(dt => <option key={dt.value} value={dt.value}>{dt.label}</option>)}
                </select>
              </div>

              <div className="col-md-4 mb_20">
                <label className="mb_8">Location</label>
                <select className="form-control" value={formData.location_id} onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}>
                  <option value="">Select Location</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>

              <div className="col-md-4 mb_20">
                <label className="mb_8">Department</label>
                <select className="form-control" value={formData.department_id} onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}>
                  <option value="">Select Department</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>

              <div className="col-md-4 mb_20">
                <label className="mb_8">Active</label>
                <div>
                  <label className="tccL fwMedium">
                    <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
                    <span className="checkmark"></span>
                    Active
                  </label>
                </div>
              </div>
            </div>

            <div className="dFlex mt_18 btnSet">
              <button type="submit" className="btnBig me-3" disabled={loading}>{loading ? 'Saving...' : (editingId ? 'Update' : 'Save')}</button>
              <button type="button" className="btnBig btnSecondary" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="baseTableWrap">
        <div className="baseTableContainer">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
          ) : (
            <table className="baseTable">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Device ID</th>
                  <th>Model</th>
                  <th>Type</th>
                  <th>Location</th>
                  <th>Dept</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {devices.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>No devices found</td>
                  </tr>
                ) : (
                  devices.map(d => (
                    <tr key={d.id}>
                      <td>{d.id}</td>
                      <td>{d.device_id}</td>
                      <td>{d.model}</td>
                      <td>{d.device_type}</td>
                      <td>{d.location_name || d.location_id || '-'}</td>
                      <td>{d.department_name || d.department_id || '-'}</td>
                      <td>{d.is_active ? 'Yes' : 'No'}</td>
                      <td>
                        <div className="dFlex optBlock">
                          <button className="optBtn" onClick={() => handleEdit(d)}>
                            <img src="/images/edit.png" alt="" />
                            <span>Edit</span>
                          </button>
                          <span className="bar">|</span>
                          <button className="optBtn" onClick={() => handleDelete(d.id)}>
                            <img src="/images/delete.png" alt="" />
                            <span>Delete</span>
                          </button>
                          <span className="bar">|</span>
                          <button className="optBtn" onClick={() => openEnrollModal(d.id)}>
                            <img src="/images/enroll.png" alt="" />
                            <span>Enroll</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {showEnroll && (
        <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Enroll Biometric Data</h5>
                <button type="button" className="close" onClick={() => setShowEnroll(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <form onSubmit={handleEnroll}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Personnel</label>
                    <select className="form-control" value={selectedPersonnelId || ''} onChange={(e) => setSelectedPersonnelId(Number(e.target.value))} required>
                      <option value="">Select Personnel</option>
                      {users.map(u => (
                        u.army_personnel && (
                          <option key={u.army_personnel.id} value={u.army_personnel.id}>{u.army_personnel.full_name} ({u.email})</option>
                        )
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Biometric Type</label>
                    <select className="form-control" value={biometricType} onChange={(e) => setBiometricType(e.target.value)}>
                      <option value="fingerprint">Fingerprint</option>
                      <option value="signature">Signature</option>
                      <option value="photo">Photo</option>
                    </select>
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    If you don't have actual device data, the server will generate simulated data for testing.
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btnSecondary" onClick={() => setShowEnroll(false)}>Cancel</button>
                  <button type="submit" className="btnPrimary">Enroll</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BiometricSettings;
